import { useState } from 'react'
import { api, ErroApi, type Profissional } from '../lib/api'
import { Folha, Erro } from '../componentes/ui'
import './ficha-profissional.css'

/**
 * A ficha do profissional.
 *
 * O cadastro antigo tinha o suficiente para a agenda funcionar: nome,
 * conselho e cor. O que falta aqui não é enfeite — cada campo destrava
 * uma coisa concreta que hoje não dá para fazer:
 *
 *   CPF e nascimento  → recibo e RPA em nome de quem atendeu
 *   e-mail e celular  → convidar para o sistema, e a clínica ligar
 *   CBO               → campo obrigatório da guia TISS SP/SADT
 *   CNS               → identificação no SUS
 *   entrada e saída   → quem atendeu em que período
 *
 * A ficha está em três blocos porque são três momentos diferentes: quem
 * é a pessoa, o que ela pode assinar, e desde quando ela trabalha aqui.
 * Só o nome é obrigatório — cadastro que exige tudo de uma vez é
 * cadastro que ninguém termina.
 */

const CONSELHOS = [
  { id: 'NENHUM',  nome: 'Nenhum' },
  { id: 'CREFITO', nome: 'CREFITO — fisioterapia' },
  { id: 'CRM',     nome: 'CRM — medicina' },
  { id: 'CRO',     nome: 'CRO — odontologia' },
  { id: 'CRP',     nome: 'CRP — psicologia' },
  { id: 'CRN',     nome: 'CRN — nutrição' },
  { id: 'COREN',  nome: 'COREN — enfermagem' },
  { id: 'CREF',    nome: 'CREF — educação física' },
  { id: 'CRF',     nome: 'CRF — farmácia' },
  { id: 'CRBM',    nome: 'CRBM — biomedicina' },
] as const

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
             'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']

/** Só dígitos — é o formato que o banco aceita, e o CHECK recusa o resto. */
const digitos = (s: string) => s.replace(/\D/g, '')

const mascaraCpf = (s: string) => {
  const d = digitos(s).slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

const mascaraTel = (s: string) => {
  const d = digitos(s).slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

type Campos = {
  nome: string; nome_curto: string; cor: string
  conselho: string; conselho_uf: string; conselho_numero: string
  cpf: string; nascimento: string; email: string; celular: string
  cbo: string; cns: string; inicio_em: string; saida_em: string
  observacao: string
}

const vazio: Campos = {
  nome: '', nome_curto: '', cor: '#0A7C87',
  conselho: 'CREFITO', conselho_uf: '', conselho_numero: '',
  cpf: '', nascimento: '', email: '', celular: '',
  cbo: '', cns: '', inicio_em: '', saida_em: '', observacao: '',
}

const de = (p: Profissional): Campos => ({
  nome: p.nome, nome_curto: p.nome_curto ?? '', cor: p.cor ?? '#0A7C87',
  conselho: p.conselho, conselho_uf: p.conselho_uf ?? '',
  conselho_numero: p.conselho_numero ?? '',
  cpf: p.cpf ? mascaraCpf(p.cpf) : '', nascimento: p.nascimento ?? '',
  email: p.email ?? '', celular: p.celular ? mascaraTel(p.celular) : '',
  cbo: p.cbo ?? '', cns: p.cns ?? '',
  inicio_em: p.inicio_em ?? '', saida_em: p.saida_em ?? '',
  observacao: p.observacao ?? '',
})

export function FichaProfissional({ profissional, aoFechar, aoSalvar }: {
  /** Nulo = cadastro novo. */
  profissional: Profissional | null
  aoFechar(): void
  aoSalvar(): Promise<void>
}) {
  const [c, setC] = useState<Campos>(profissional ? de(profissional) : vazio)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const set = (k: keyof Campos) => (v: string) => setC((x) => ({ ...x, [k]: v }))

  const exigeConselho = c.conselho !== 'NENHUM'

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      // Só manda o que foi preenchido: string vazia não é "apague o
      // campo", e mandar '' onde o banco espera date dá erro de tipo.
      const corpo: Record<string, unknown> = {
        nome: c.nome.trim(), conselho: c.conselho, cor: c.cor,
      }
      const talvez = (k: string, v: string) => { if (v.trim()) corpo[k] = v.trim() }
      talvez('nome_curto', c.nome_curto)
      talvez('conselho_uf', c.conselho_uf)
      talvez('conselho_numero', c.conselho_numero)
      talvez('email', c.email)
      talvez('nascimento', c.nascimento)
      talvez('inicio_em', c.inicio_em)
      talvez('saida_em', c.saida_em)
      talvez('observacao', c.observacao)
      // Estes vão só com dígitos — é o formato que o CHECK do banco exige.
      if (digitos(c.cpf))     corpo.cpf     = digitos(c.cpf)
      if (digitos(c.celular)) corpo.celular = digitos(c.celular)
      if (digitos(c.cbo))     corpo.cbo     = digitos(c.cbo)
      if (digitos(c.cns))     corpo.cns     = digitos(c.cns)

      if (profissional) {
        await api(`/v1/profissionais/${profissional.id}`, { metodo: 'PATCH', corpo })
      } else {
        await api('/v1/profissionais', { metodo: 'POST', corpo })
      }
      await aoSalvar()
    } catch (err) {
      setErro(
        err instanceof ErroApi && err.status === 409
          ? 'Já existe um profissional com este CPF nesta clínica.'
          : err instanceof ErroApi && err.status === 422
          ? 'Confira CPF, CBO, CNS e as datas — algum campo está fora do formato.'
          : err instanceof ErroApi ? err.message
          : 'Não foi possível salvar.',
      )
      setEnviando(false)
    }
  }

  return (
    <Folha
      titulo={profissional ? c.nome || 'Profissional' : 'Novo profissional'}
      descricao="Só o nome é obrigatório. O resto pode entrar depois."
      aoFechar={aoFechar}
      acoes={
        <>
          <button type="button" className="botao botao--fantasma" onClick={aoFechar}>Cancelar</button>
          <button className="botao" type="submit" form="form-prof"
                  disabled={enviando || c.nome.trim().length < 2}>
            {enviando ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="form-prof" onSubmit={submeter} className="fp">
        <Erro mensagem={erro} />

        {/* ── quem é ─────────────────────────────────────────── */}
        <fieldset className="fp__bloco">
          <legend>Quem é</legend>

          <div className="campo">
            <label htmlFor="fp-nome">Nome completo</label>
            <input id="fp-nome" className="entrada" required autoFocus maxLength={120}
                   value={c.nome} onChange={(e) => set('nome')(e.target.value)} />
          </div>

          <div className="fp__dupla">
            <div className="campo">
              <label htmlFor="fp-curto">Como aparece na agenda</label>
              <input id="fp-curto" className="entrada" maxLength={40}
                     placeholder={c.nome.split(' ')[0] || 'primeiro nome'}
                     value={c.nome_curto} onChange={(e) => set('nome_curto')(e.target.value)} />
              <span className="ajuda">Coluna estreita cabe pouco texto.</span>
            </div>
            <div className="campo">
              <label htmlFor="fp-cor">Cor na agenda</label>
              <input id="fp-cor" className="entrada fp__cor" type="color"
                     value={c.cor} onChange={(e) => set('cor')(e.target.value)} />
            </div>
          </div>

          <div className="fp__dupla">
            <div className="campo">
              <label htmlFor="fp-cpf">CPF</label>
              <input id="fp-cpf" className="entrada mono" inputMode="numeric"
                     value={c.cpf} onChange={(e) => set('cpf')(mascaraCpf(e.target.value))} />
              <span className="ajuda">Vai no recibo e no RPA.</span>
            </div>
            <div className="campo">
              <label htmlFor="fp-nasc">Nascimento</label>
              <input id="fp-nasc" className="entrada" type="date"
                     value={c.nascimento} onChange={(e) => set('nascimento')(e.target.value)} />
            </div>
          </div>

          <div className="fp__dupla">
            <div className="campo">
              <label htmlFor="fp-email">E-mail</label>
              <input id="fp-email" className="entrada" type="email" inputMode="email"
                     value={c.email} onChange={(e) => set('email')(e.target.value)} />
              <span className="ajuda">Usado para convidar ao sistema.</span>
            </div>
            <div className="campo">
              <label htmlFor="fp-cel">Celular</label>
              <input id="fp-cel" className="entrada mono" inputMode="tel"
                     value={c.celular} onChange={(e) => set('celular')(mascaraTel(e.target.value))} />
            </div>
          </div>
        </fieldset>

        {/* ── o que pode assinar ─────────────────────────────── */}
        <fieldset className="fp__bloco">
          <legend>O que pode assinar</legend>

          <div className="campo">
            <label htmlFor="fp-cons">Conselho</label>
            <select id="fp-cons" className="entrada" value={c.conselho}
                    onChange={(e) => set('conselho')(e.target.value)}>
              {CONSELHOS.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
            </select>
            <span className="ajuda">
              {exigeConselho
                ? 'Número e UF ficam obrigatórios — é o carimbo da evolução assinada.'
                : 'Sem conselho: pode aparecer na agenda, mas não assina prontuário.'}
            </span>
          </div>

          {exigeConselho && (
            <div className="fp__dupla">
              <div className="campo">
                <label htmlFor="fp-num">Número do registro</label>
                <input id="fp-num" className="entrada" maxLength={30} required={exigeConselho}
                       value={c.conselho_numero}
                       onChange={(e) => set('conselho_numero')(e.target.value)} />
              </div>
              <div className="campo">
                <label htmlFor="fp-uf">UF</label>
                <select id="fp-uf" className="entrada" required={exigeConselho}
                        value={c.conselho_uf} onChange={(e) => set('conselho_uf')(e.target.value)}>
                  <option value="">—</option>
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          )}

          <div className="fp__dupla">
            <div className="campo">
              <label htmlFor="fp-cbo">CBO</label>
              <input id="fp-cbo" className="entrada mono" inputMode="numeric" maxLength={6}
                     placeholder="223605"
                     value={c.cbo} onChange={(e) => set('cbo')(digitos(e.target.value))} />
              <span className="ajuda">Seis dígitos. Obrigatório na guia de convênio.</span>
            </div>
            <div className="campo">
              <label htmlFor="fp-cns">CNS</label>
              <input id="fp-cns" className="entrada mono" inputMode="numeric" maxLength={15}
                     value={c.cns} onChange={(e) => set('cns')(digitos(e.target.value))} />
              <span className="ajuda">Quinze dígitos. Só para quem atende SUS.</span>
            </div>
          </div>
        </fieldset>

        {/* ── desde quando ───────────────────────────────────── */}
        <fieldset className="fp__bloco">
          <legend>Desde quando trabalha aqui</legend>
          <div className="fp__dupla">
            <div className="campo">
              <label htmlFor="fp-ini">Entrada</label>
              <input id="fp-ini" className="entrada" type="date"
                     value={c.inicio_em} onChange={(e) => set('inicio_em')(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="fp-sai">Saída</label>
              <input id="fp-sai" className="entrada" type="date"
                     value={c.saida_em} onChange={(e) => set('saida_em')(e.target.value)} />
              {/* Preencher a saída NÃO apaga nada: os atendimentos, as
                  evoluções assinadas e a comissão continuam no histórico
                  com o nome dela. */}
              <span className="ajuda">Preencher não apaga o histórico dela.</span>
            </div>
          </div>
          <div className="campo">
            <label htmlFor="fp-obs">Observação interna</label>
            <input id="fp-obs" className="entrada" maxLength={500}
                   value={c.observacao} onChange={(e) => set('observacao')(e.target.value)} />
          </div>
        </fieldset>
      </form>
    </Folha>
  )
}
